// Pure rules of the "New revision" form (SPEC §4.6).
import {
  DocumentCraftingFormField,
  DocumentCraftingRevisionSummary,
  DocumentCraftingUpload,
} from "../../../Models/documentCrafting";
import { FormValues, formUploads } from "../Create/documentFormModel";

/** Below this many characters a change request counts as "small". */
export const SMALL_CHANGE_REQUEST_LIMIT = 160;

export const CHANGE_REQUEST_MAX_LENGTH = 4000;

/** Fields the revise form shows; the others (the briefing) are history. */
export const editableInRevision = (fields: DocumentCraftingFormField[]) =>
  fields.filter((field) => field.editable_in_revision !== false);

// Comparable form of a value: trimmed texts, and empty texts, lists, objects
// and unticked checkboxes all count as "nothing".
const comparable = (value: unknown): unknown => {
  if (value === null || value === undefined || value === false) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  if (Array.isArray(value)) {
    const items = value.map(comparable);
    return items.every((item) => item === null) ? null : items;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    Object.keys(record)
      .sort()
      .forEach((key) => {
        const item = comparable(record[key]);
        if (item !== null) result[key] = item;
      });
    return Object.keys(result).length ? result : null;
  }
  return value;
};

/** Whether a structured (non-file) field differs between two value sets. */
export const structuredFieldsChanged = (
  fields: DocumentCraftingFormField[],
  before: FormValues,
  after: FormValues,
) =>
  fields
    .filter((field) => field.type !== "file")
    .some(
      (field) =>
        JSON.stringify(comparable(before[field.key])) !==
        JSON.stringify(comparable(after[field.key])),
    );

export interface ChangeSummary {
  changeRequest: string;
  /** Structured fields differ from the base revision. */
  fieldsChanged: boolean;
  newFiles: number;
  /** Earlier uploads left out of this run. */
  untickedUploads: number;
}

/**
 * The soft "only a small change?" rule: a short change request and nothing
 * else changed. The form then suggests editing the DOCX/PPTX by hand.
 */
export const isSmallChange = (summary: ChangeSummary) =>
  summary.changeRequest.trim().length < SMALL_CHANGE_REQUEST_LIMIT &&
  !summary.fieldsChanged &&
  summary.newFiles === 0 &&
  summary.untickedUploads === 0;

export interface HistoryEntry {
  revisionId: number;
  number: number | null;
  /** Change request (or the note of a manual upload) of the revision. */
  changeRequest: string | null;
  refinements: string[];
  revision: DocumentCraftingRevisionSummary;
}

/** Earlier change requests up to the base revision, oldest first. */
export const changeRequestHistory = (
  revisions: DocumentCraftingRevisionSummary[],
  baseNumber: number | null,
): HistoryEntry[] =>
  revisions
    .filter(
      (revision) =>
        revision.number !== null &&
        (baseNumber === null || revision.number <= baseNumber),
    )
    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
    .map((revision) => ({
      revisionId: revision.id,
      number: revision.number,
      changeRequest: revision.change_request?.trim() || null,
      refinements: revision.refinements
        .map((item) => item.change_request?.trim())
        .filter((text): text is string => !!text),
      revision,
    }))
    .filter((entry) => entry.changeRequest || entry.refinements.length);

/** Earlier uploads that are not used for this run: every upload of the base
 * revision that is neither ticked in a file field nor kept as "other". */
export const untickedUploadIds = (
  uploads: DocumentCraftingUpload[],
  selectedIds: number[],
  keptIds: number[] = [],
) =>
  formUploads(uploads)
    .map((upload) => upload.id)
    .filter((id) => !selectedIds.includes(id) && !keptIds.includes(id));

/** How many uploads the last run used that are now unticked. */
export const countNewlyUnticked = (initialIds: number[], currentIds: number[]) =>
  initialIds.filter((id) => !currentIds.includes(id)).length;
